import { apiClient } from './client';
import type { ApiResponse } from '@/types/api.types';

// Engine role → GL account bindings (cfg_account_role_map): company defaults + per-cost-centre overrides.
/** VOUCHER_GROUP: a voucher category's own account (e.g. gift vouchers sold per category). */
export type RoleScopeType = 'COMPANY' | 'COST_CENTER' | 'VOUCHER_GROUP';

export interface RoleAccountDto {
  id: string;
  scopeType: RoleScopeType;
  /** The voucher category, for VOUCHER_GROUP. */
  scopeCode?: string | null;
  costCenterId?: string | null;
  costCenterCode?: string | null;
  costCenterName?: string | null;
  accountRole: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  isActive: boolean;
}

export interface SaveRoleAccountRequest {
  id?: string | null;
  scopeType: RoleScopeType;
  costCenterId?: string | null;
  scopeCode?: string | null;
  accountRole: string;
  accountId: string;
}

export interface PaymentModeAccountDto {
  id: string;
  paymentModeCode: string;
  accountRole: string;
  costCenterId?: string | null;
  costCenterCode?: string | null;
  accountId: string;
  accountCode: string;
  accountName: string;
  mdrRate?: number | null;
}

export interface SavePaymentModeAccountRequest {
  paymentModeCode: string;
  accountRole: string;
  costCenterId?: string | null;
  accountId: string;
  mdrRate?: number | null;
}

/** A role the company's live posting rules post to, whether it is mapped, and which rules use it. */
export interface RequiredRoleDto {
  role: string;
  mapped: boolean;
  usedBy: string[];
}

export const roleAccountApi = {
  async required(): Promise<RequiredRoleDto[]> {
    const r = await apiClient.get<ApiResponse<RequiredRoleDto[]>>('/config/role-accounts/required');
    return r.data?.data ?? [];
  },
  async list(): Promise<RoleAccountDto[]> {
    const r = await apiClient.get('/config/role-accounts');
    return r.data?.data ?? [];
  },
  async upsert(req: SaveRoleAccountRequest): Promise<RoleAccountDto> {
    const r = await apiClient.post('/config/role-accounts', req);
    return r.data?.data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/config/role-accounts/${id}`);
  },

  // Payment-mode → account bindings (cash-in-hand / clearing / MDR, optionally per cost centre).
  async paymentModes(): Promise<PaymentModeAccountDto[]> {
    const r = await apiClient.get('/config/role-accounts/payment-modes');
    return r.data?.data ?? [];
  },
  async upsertPaymentMode(req: SavePaymentModeAccountRequest): Promise<PaymentModeAccountDto> {
    const r = await apiClient.post('/config/role-accounts/payment-modes', req);
    return r.data?.data;
  },
  async removePaymentMode(id: string): Promise<void> {
    await apiClient.delete(`/config/role-accounts/payment-modes/${id}`);
  },
};

// Common payment-mode account roles.
export const PAYMENT_MODE_ROLES = ['CLEARING', 'MDR_EXPENSE'];

// Common engine roles (from the seeded account-role map) for the picker.
export const ACCOUNT_ROLES = [
  'CASH', 'BANK', 'CARD_CLEARING', 'MDR_EXPENSE', 'REVENUE', 'COGS', 'INVENTORY',
  'AP', 'AR', 'INPUT_TAX', 'OUTPUT_TAX', 'ROUNDING', 'ADJUSTMENT_GAIN', 'ADJUSTMENT_LOSS',
  'WASTAGE_EXPENSE', 'VOUCHER_LIABILITY', 'VOUCHER_BREAKAGE', 'LOYALTY_LIABILITY', 'LOYALTY_EXPENSE', 'DEPOSIT_LIABILITY',
  // The four exchange-difference roles (QC Part III item 2).
  //
  // AllocationService resolves FX_GAIN / FX_LOSS when a receipt or payment settles an invoice at a
  // different rate, and refuses the posting when neither is mapped — "Configure it in Posting
  // Accounts". This list is what that screen offers, and it did not offer them, so the advice
  // named a screen on which the fix could not be made. FxRevaluationService resolves the
  // unrealized pair at period end for the same reason.
  'FX_GAIN', 'FX_LOSS', 'UNREALIZED_FX_GAIN', 'UNREALIZED_FX_LOSS',
];

/** Plain-language names for the engine roles; the code stays the value saved. */
export const ROLE_LABELS: Record<string, string> = {
  CASH: 'Cash in hand',
  BANK: 'Bank',
  CARD_CLEARING: 'Card clearing (money due from the card acquirer)',
  MDR_EXPENSE: 'Card fees (merchant discount)',
  REVENUE: 'Sales revenue',
  COGS: 'Cost of sales',
  INVENTORY: 'Inventory',
  AP: 'Supplier control (payables)',
  AR: 'Customer control (receivables)',
  INPUT_TAX: 'Input tax (VAT on purchases)',
  OUTPUT_TAX: 'Output tax (VAT on sales)',
  ROUNDING: 'Rounding',
  ADJUSTMENT_GAIN: 'Stock adjustment gain',
  ADJUSTMENT_LOSS: 'Stock adjustment loss',
  WASTAGE_EXPENSE: 'Stock wastage',
  VOUCHER_LIABILITY: 'Voucher liability (vouchers sold, not yet used)',
  VOUCHER_BREAKAGE: 'Voucher breakage income',
  LOYALTY_LIABILITY: 'Loyalty points liability',
  LOYALTY_EXPENSE: 'Loyalty programme expense',
  DEPOSIT_LIABILITY: 'Customer deposits',
  CHARGEBACK: 'Card chargebacks',
  SETTLEMENT_VARIANCE: 'Card settlement adjustments',
  FX_GAIN: 'Exchange gain (realised)',
  FX_LOSS: 'Exchange loss (realised)',
  UNREALIZED_FX_GAIN: 'Exchange gain (unrealised)',
  UNREALIZED_FX_LOSS: 'Exchange loss (unrealised)',
  GRNI: 'Goods received, not yet invoiced',
};

/** "Voucher breakage income", or the code itself for a role with no plain name yet. */
export const roleLabel = (role: string) => ROLE_LABELS[role] ?? role;
